import React, { useState, useEffect, Fragment, useContext } from 'react';
import styles from './styles.module.css';
import CloseIcon from '@mui/icons-material/Close';
import { TextField, Grid, Button, CircularProgress, IconButton } from '@mui/material';
import SubscriptionService from 'services/SubscriptionService';
import { STORAGE_KEYS } from 'const';
import { sentenceCase } from 'change-case';
import { useTranslation } from 'react-i18next';
import { ShopContext } from 'Context/ShopContext';
import { SANOFT_DEALER } from 'const';

function KeyRenewPopup({ handleClose, handleKeyRegister }) {
  const { t: tCommon } = useTranslation('common');
  const { t: tPopup } = useTranslation('popup');
  const [key, setKey] = useState();
  const [message, setMessage] = useState(null);
  const [tokenData, setTokenData] = useState(null);
  const [prevKey, setPrevKey] = useState(null);
  const [loading, setLoading] = useState(false);
  const { shop } = useContext(ShopContext);

  const verifyKeyHandler = async () => {
    setMessage(null);
    setLoading(true);
    try {
      if (!key || key.length < 10) {
        setMessage({ error: tPopup('Please enter a valid coupon.') });
        setLoading(false);
        return;
      }
      const resp = await SubscriptionService.verifyRegistrationKey(key);

      if (resp.dealer && shop?.dealer?.name !== SANOFT_DEALER && shop?.dealer?.name !== resp.dealer.name) {
        setMessage({ error: tPopup('This token is not valid for your current dealer') });
        setLoading(false);
        return;
      }

      setMessage({ success: tPopup('Coupon verified.') });
      setTokenData(resp?.plan);
      setPrevKey(key);
      localStorage.setItem(STORAGE_KEYS.REGISTRSATION_KEY, key);
    } catch (error) {
      setMessage({ error: error.message });
    }
    setLoading(false);
  };

  const [proceedLoading, setProceedLoading] = useState(false);

  const proceedHandler = async () => {
    setProceedLoading(true);
    try {
      await handleKeyRegister(tokenData);
    } catch (error) {
      console.error(error);
    }
    setProceedLoading(false);
  };

  useEffect(() => {
    if (!tokenData || key === prevKey) return;
    setTokenData(null);
    //eslint-disable-next-line
  }, [key, prevKey]);

  return (
    <div className={styles.popUp} style={{ zIndex: 1300 }}>
      <div className={styles.contentWrapper} style={{ paddingBottom: 5 }}>
        <div className={styles.headSec}>
          <h2 className={styles.editTitle}>{tPopup('Sign up with key')}</h2>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </div>
        <div className={styles.inputSec}>
          <Grid container rowSpacing={2}>
            <Grid item xs={12}>
              <span className={styles.subTextSec}>
                {tPopup(
                  'Verify your key. If it is valid, it will display the subscriptions and add-ons available. These options are specified and not changeable.'
                )}
              </span>
            </Grid>
            <Grid item xs={12} display="flex">
              <TextField
                variant="outlined"
                size="small"
                fullWidth
                className={styles.numberInput}
                type="text"
                value={key || ''}
                onChange={(e) => {
                  setKey(e.target.value);
                }}
              />
              <Button
                variant="contained"
                color="success"
                size="small"
                sx={{
                  ml: 1.25,
                  px: 2.5,
                  minWidth: 90,
                  bgcolor: '#00a65a',
                  '&:hover': {
                    bgcolor: '#00a65a',
                  },
                }}
                onClick={verifyKeyHandler}
                disabled={loading}
              >
                {loading ? <CircularProgress size={18} color="inherit" /> : tCommon('Verify')}
              </Button>
            </Grid>
            <Grid item xs={12} display="flex" justifyContent="center">
              {message ? (
                <span className={styles.errorMsg}>{message?.error}</span>
              ) : (
                <span className={styles.successMsg}>{message?.success}</span>
              )}
            </Grid>
            {tokenData && (
              <Grid item container rowSpacing={2} xs={12}>
                <Grid item xs={12}>
                  <span className={styles.editTitle}>{tCommon('Subscription')}</span>
                </Grid>
                {tokenData?.subscription?.key ? (
                  <Grid item container rowSpacing={1} xs={12}>
                    <Grid item xs={6}>
                      <span className={styles.text}>{tCommon('Name')}</span>
                    </Grid>
                    <Grid item xs={6}>
                      <span className={styles.text}>{sentenceCase(tokenData?.subscription?.key || '')}</span>
                    </Grid>
                    <Grid item xs={6}>
                      <span className={styles.text}>{tCommon('Validity')}</span>
                    </Grid>
                    <Grid item xs={6}>
                      <span className={styles.text}>
                        {tokenData?.subscription?.validity} {tCommon('days')}
                      </span>
                    </Grid>
                  </Grid>
                ) : (
                  <Grid item rowSpacing={1} xs={12} textAlign="center">
                    <span className={styles.suTtext}>{tPopup('There is no subscription in this token')}</span>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <span className={styles.editTitle}>{tCommon('Add-ons')}</span>
                </Grid>
                <Grid item container rowSpacing={1} xs={12}>
                  {(tokenData?.add_ons || []).map((addon) => {
                    return (
                      <Fragment key={addon.key}>
                        <Grid item xs={6}>
                          <span className={styles.text}>{sentenceCase(addon?.key)}</span>
                        </Grid>
                        <Grid item xs={6}>
                          {addon.key === 'ecs_terminal' || addon.key === 'pos_terminal' ? (
                            <span className={styles.text}>{addon?.count}</span>
                          ) : (
                            <span className={styles.text}>
                              {addon?.validity} {tCommon('days')}
                            </span>
                          )}
                        </Grid>
                      </Fragment>
                    );
                  })}
                </Grid>
                <Grid item xs={12} mt={1}>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={proceedHandler}
                    disabled={proceedLoading}
                    sx={{ height: 36.5 }} // Ensure standard button height doesn't collapse
                  >
                    {proceedLoading ? <CircularProgress size={20} color="inherit" /> : tCommon('Proceed')}
                  </Button>
                </Grid>
              </Grid>
            )}
          </Grid>
        </div>
      </div>
    </div>
  );
}

export default KeyRenewPopup;
